import { useLazyQuery, useMutation } from '@apollo/client';
import React, { useCallback, useState } from 'react';
import { Alert, Button, Form, Spinner, Table } from 'react-bootstrap';

import {
  GET_USER_MONTHLY_PASS_CLAIMED_REWARDS,
  PREVIEW_MONTHLY_PASS_AUTO_CLAIM,
  RESET_USER_MONTHLY_PASS_CLAIMED_REWARDS,
  SEED_MONTHLY_PASS_STATE_FOR_TEST,
  TRIGGER_MONTHLY_PASS_AUTO_CLAIM,
} from '../../../graphql';

type ClaimablePreviewRow = {
  level: number | null;
  tier: string | null;
  type: string;
  amount: number | null;
  itemId: string | null;
};

type ClaimedReward = {
  level: number;
  bestTierClaimed: string | null;
  tiersClaimed: string[];
};

type Props = {
  userId: string;
  // Owned by <MonthlyPassDisplay>: the month picked in this sub-section's own selector
  monthId: string;
};

const MonthlyPassRewardsDisplay: React.FC<Props> = ({ userId, monthId }) => {
  const [getClaimedRewards, { data, loading }] = useLazyQuery(GET_USER_MONTHLY_PASS_CLAIMED_REWARDS, {
    fetchPolicy: 'network-only',
  });
  const [resetClaimedRewards, { loading: resetting }] = useMutation(RESET_USER_MONTHLY_PASS_CLAIMED_REWARDS);
  const [triggerAutoClaim, { loading: autoClaiming }] = useMutation(TRIGGER_MONTHLY_PASS_AUTO_CLAIM);
  const [seedState, { loading: seeding }] = useMutation(SEED_MONTHLY_PASS_STATE_FOR_TEST);
  const [previewAutoClaim, { loading: previewing }] = useMutation(PREVIEW_MONTHLY_PASS_AUTO_CLAIM);

  const [resetGuard, setResetGuard] = useState(true);
  const [autoClaimResult, setAutoClaimResult] = useState<{ ok: boolean; message: string } | null>(null);
  const [seedLevel, setSeedLevel] = useState('');
  const [seedResult, setSeedResult] = useState<{ ok: boolean; message: string } | null>(null);
  const [preview, setPreview] = useState<ClaimablePreviewRow[] | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);

  const userMonthlyPass = data?.app?.getUserMonthlyPass?.userMonthlyPass;
  const claimedRewards: ClaimedReward[] = userMonthlyPass?.claimedRewards ?? [];
  // Every track the user has claimed at least once, to offer "reset this track on all levels".
  const claimedTiers = Array.from(new Set(claimedRewards.flatMap((reward) => reward.tiersClaimed)));

  // The trigger requires an ENDED season. Compare against the current YYYY-MM (approx, UTC) so
  // the button self-disables for the current/future month; the BE still validates authoritatively.
  const currentMonthApprox = new Date().toISOString().slice(0, 7);
  const isEndedSeason = Boolean(monthId) && monthId < currentMonthApprox;

  const showRewards = useCallback(() => {
    getClaimedRewards({ variables: { input: { monthId, userId } } });
  }, [getClaimedRewards, monthId, userId]);

  const fireAutoClaim = useCallback(async () => {
    setAutoClaimResult(null);
    try {
      const { data: result } = await triggerAutoClaim({ variables: { input: { userId, monthId, resetGuard } } });
      const payload = result?.triggerMonthlyPassAutoClaim;
      const grantedCount = payload?.grantedCount ?? 0;
      setAutoClaimResult({
        ok: true,
        message: `Auto-claim ran for ${monthId}: granted ${grantedCount} reward(s).${
          grantedCount === 0 ? ' (Nothing unclaimed — un-claim some rewards first, then re-fire.)' : ''
        } autoClaimedAt: ${payload?.autoClaimedAt ?? 'null'}`,
      });
      // Refresh the claimed-rewards table so QA sees the new state immediately.
      getClaimedRewards({ variables: { input: { monthId, userId } } });
    } catch (error) {
      setAutoClaimResult({ ok: false, message: (error as Error).message });
    }
  }, [triggerAutoClaim, userId, monthId, resetGuard, getClaimedRewards]);

  const seedClaimable = useCallback(async () => {
    setSeedResult(null);
    const trimmed = seedLevel.trim();
    const levelArg = trimmed === '' ? undefined : Number(trimmed);
    if (levelArg !== undefined && (!Number.isInteger(levelArg) || levelArg < 1)) {
      setSeedResult({ ok: false, message: 'Level must be a whole number ≥ 1, or blank for max level.' });
      return;
    }
    try {
      const { data: result } = await seedState({ variables: { input: { userId, monthId, level: levelArg } } });
      const payload = result?.seedMonthlyPassStateForTest;
      setSeedResult({
        ok: true,
        message: `Set ${monthId} to level ${payload?.level ?? '?'} (max ${payload?.maxLevel ?? '?'}) with all rewards unclaimed — fire auto-claim below.`,
      });
      getClaimedRewards({ variables: { input: { monthId, userId } } });
    } catch (error) {
      setSeedResult({ ok: false, message: (error as Error).message });
    }
  }, [seedState, userId, monthId, seedLevel, getClaimedRewards]);

  const showPreview = useCallback(async () => {
    setPreviewError(null);
    try {
      const { data: result } = await previewAutoClaim({ variables: { input: { userId, monthId } } });
      setPreview((result?.previewMonthlyPassAutoClaim ?? []) as ClaimablePreviewRow[]);
    } catch (error) {
      setPreview(null);
      setPreviewError((error as Error).message);
    }
  }, [previewAutoClaim, userId, monthId]);

  const reset = useCallback(
    (levels?: number[], tiers?: string[]) => {
      const levelScope = levels ? `level ${levels.join(', ')}` : 'ALL levels';
      const tierScope = tiers ? `${tiers.join(', ')} track` : 'all tracks';
      if (
        !window.confirm(
          `Un-claim ${levelScope} (${tierScope}) of monthly pass ${monthId} for this user? Already granted items/currency are NOT taken back.`
        )
      ) {
        return;
      }
      resetClaimedRewards({ variables: { input: { userId, monthId, levels, tiers } } });
    },
    [resetClaimedRewards, userId, monthId]
  );

  return (
    <>
      <div className="d-flex justify-content-start align-items-baseline flex-wrap mb-3" style={{ gap: 8 }}>
        <Button size="sm" onClick={showRewards} disabled={loading}>
          {loading ? <Spinner animation="border" size="sm" /> : 'Show claimed rewards'}
        </Button>
        {claimedTiers.map((tier) => (
          <Button
            key={tier}
            size="sm"
            variant="warning"
            disabled={resetting}
            title={`Un-claim the ${tier} track on every level of this month`}
            onClick={() => reset(undefined, [tier])}
          >
            Reset all {tier}
          </Button>
        ))}
        <Button size="sm" variant="danger" onClick={() => reset()} disabled={resetting || claimedRewards.length === 0}>
          {resetting ? <Spinner animation="border" size="sm" /> : 'Reset everything'}
        </Button>
      </div>

      {/* QA: simulate the ended-season auto-claim (same grant the 1st-of-month cron performs). */}
      <div className="mb-3 p-2" style={{ border: '1px dashed #bbb', borderRadius: 6 }}>
        {/* Step 1 (optional): force this user into a claimable state for the ended season. */}
        <div className="d-flex align-items-end flex-wrap mb-2" style={{ gap: 12 }}>
          <Form.Group className="mb-0" style={{ width: 160 }}>
            <Form.Label className="mb-1" style={{ fontSize: 12 }}>
              Level (blank = max)
            </Form.Label>
            <Form.Control
              size="sm"
              type="number"
              min={1}
              placeholder="max"
              value={seedLevel}
              onChange={(e) => setSeedLevel(e.target.value)}
            />
          </Form.Group>
          <Button
            size="sm"
            variant="outline-primary"
            onClick={seedClaimable}
            disabled={seeding || !isEndedSeason}
            title="Set this user's level for the month and leave every earned reward unclaimed"
          >
            {seeding ? <Spinner animation="border" size="sm" /> : 'Set claimable unclaimed rewards'}
          </Button>
        </div>
        <div className="text-muted mb-2" style={{ fontSize: 12 }}>
          Leave <b>Level blank</b> to make <b>every</b> level of the pass claimable (max level); enter a number to
          simulate a partial season (e.g. <code>5</code> = levels 1–5 claimable).
        </div>
        {seedResult && (
          <Alert variant={seedResult.ok ? 'success' : 'danger'} className="mb-2 py-2">
            {seedResult.message}
          </Alert>
        )}

        {/* Preview: what the auto-claim would grant right now (read-only). */}
        <div className="mb-2">
          <Button
            size="sm"
            variant="outline-secondary"
            onClick={showPreview}
            disabled={previewing || !isEndedSeason}
            title="List the earned-but-unclaimed rewards the auto-claim would grant (no changes made)"
          >
            {previewing ? <Spinner animation="border" size="sm" /> : 'Preview claimable rewards'}
          </Button>
          {previewError && (
            <Alert variant="danger" className="mt-2 mb-0 py-2">
              {previewError}
            </Alert>
          )}
          {preview && (
            <div className="mt-2" style={{ maxHeight: '35vh', overflowY: 'auto' }}>
              {preview.length === 0 ? (
                <div className="text-muted">No claimable rewards — seed a level or un-claim some rewards first.</div>
              ) : (
                <Table striped bordered hover size="sm" className="mb-0">
                  <thead>
                    <tr>
                      <th>Level</th>
                      <th>Tier</th>
                      <th>Type</th>
                      <th>Amount</th>
                      <th>Item</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.map((row, i) => (
                      <tr key={`${row.level}-${row.tier}-${row.type}-${i}`}>
                        <td>{row.level ?? '-'}</td>
                        <td>{row.tier ?? '-'}</td>
                        <td>{row.type}</td>
                        <td>{row.amount ?? '-'}</td>
                        <td>{row.itemId ?? '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </div>
          )}
        </div>

        {/* Step 2: fire the auto-claim. */}
        <div className="d-flex align-items-center flex-wrap" style={{ gap: 12 }}>
          <Button
            size="sm"
            variant="success"
            onClick={fireAutoClaim}
            disabled={autoClaiming || !isEndedSeason}
            title={
              isEndedSeason
                ? `Run the auto-claim for ${monthId} — grants every earned-but-unclaimed reward`
                : 'Auto-claim only runs on an ENDED season — pick a previous month'
            }
          >
            {autoClaiming ? <Spinner animation="border" size="sm" /> : 'Fire auto-claim (ended season)'}
          </Button>
          <Form.Check
            type="checkbox"
            id="mp-autoclaim-reset-guard"
            label="Force re-run (clear autoClaimedAt guard first)"
            checked={resetGuard}
            onChange={(e) => setResetGuard(e.target.checked)}
          />
        </div>
        {!isEndedSeason && (
          <div className="mt-2 text-muted" style={{ fontSize: 12 }}>
            Selected month <b>{monthId || '—'}</b> is not an ended season. Pick a previous month (before{' '}
            {currentMonthApprox}) to enable the trigger.
          </div>
        )}
        {autoClaimResult && (
          <Alert variant={autoClaimResult.ok ? 'success' : 'danger'} className="mt-2 mb-0 py-2">
            {autoClaimResult.message}
          </Alert>
        )}
      </div>

      {userMonthlyPass && (
        <div className="mb-2">
          Level {userMonthlyPass.level} — {userMonthlyPass.progressToNextLevel}% to next level — xpBuff x
          {userMonthlyPass.xpBuff}, coinBuff x{userMonthlyPass.coinBuff}
        </div>
      )}

      {userMonthlyPass && claimedRewards.length === 0 ? (
        <div>No claimed level reward for this month</div>
      ) : (
        claimedRewards.length > 0 && (
          <div style={{ maxHeight: '50vh', overflowY: 'auto' }}>
            <Table striped bordered hover size="sm">
              <thead>
                <tr>
                  <th>Level</th>
                  <th>Best tier claimed</th>
                  <th>Tiers claimed</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {claimedRewards.map((reward) => (
                  <tr key={reward.level}>
                    <td>{reward.level}</td>
                    <td>{reward.bestTierClaimed ?? '-'}</td>
                    <td>{reward.tiersClaimed.join(', ')}</td>
                    <td>
                      <div className="d-flex" style={{ gap: 4 }}>
                        {reward.tiersClaimed.map((tier) => (
                          <Button
                            key={tier}
                            size="sm"
                            variant="warning"
                            disabled={resetting}
                            title={`Un-claim only the ${tier} track of level ${reward.level}`}
                            onClick={() => reset([reward.level], [tier])}
                          >
                            Reset {tier}
                          </Button>
                        ))}
                        {reward.tiersClaimed.length > 1 && (
                          <Button
                            size="sm"
                            variant="danger"
                            disabled={resetting}
                            title={`Un-claim every track of level ${reward.level}`}
                            onClick={() => reset([reward.level])}
                          >
                            Reset all tracks
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )
      )}
    </>
  );
};

export default MonthlyPassRewardsDisplay;
